/* eslint-disable @typescript-eslint/no-unused-vars */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, combineLatest, map, mergeMap, Observable, of, tap, throwError } from 'rxjs';
import { SessionService } from 'src/app/services/session.service';
import { environment } from 'src/environments/environment';
import { FormWizardDataModel, IFormWizardDataModel, IWizardDataState } from '../models/form-wizard-models';
import { CategoryService } from './category-service';
import { ExchangeAreaService } from './exchange-area.service';
import { PurposeService } from './purpose-service';
import { SelfAuditService } from './self-audit.service';
import { NotificationAreaService } from './notification-area-service';
import {
  ILandholderConfirmationModel, ILotPlanModel,
  ILandholderDetailsModel, IPurposeDataModel,
  ICategoryPurposeDataModel,
  INotificationAreaModel,
  ISelfAuditDataModel,
  IExchangeAreaDataModel,
  IActivityStorageDataModel,
  IRegionalEcosystemDataModel,
  ISetAreaResponseModel,
  IDeclarationsDataModel,
  IRegionalEcosystemStatusModel,
  IChannelDimensionsDataModel,
  IAcknowledgementsDataModel,
  IReasonModel,
  INotifierDetailsModel,
  IRegionalEcosystemStatusesResponseModel,
  IAcknowledgementNotificationDataModel,
  IChannelDimensionsModel,
} from '../models/veg-wizard-models';
import { Logger } from './logger-service';
import { ActivityService } from './activity-service';
import { RegionalEcosystemStatusService } from './regional-ecosystem-status.service';
import { ChannelDimensionsService } from './channel-dimensions.service';
import { DeclarationsService } from './declarations.service';
import { AcknowledgementService } from './acknowledgement.service';
import { ContactService } from './contact.service';
import { ReasonService } from './reason.service';
import { UtilService } from './util.service';
import { RegionalEcosystemService } from './regional-ecosystem.service';


@Injectable({
  providedIn: 'root'
})
export class VegWizardDataService {
  private formWizardDataModel: IFormWizardDataModel;

  constructor(
    private utilService: UtilService,
    private sessionService: SessionService,
    private contactService: ContactService,
    private exchangeAreaService: ExchangeAreaService,
    private selfAuditService: SelfAuditService,
    private notificationAreaService: NotificationAreaService,
    private categoryService: CategoryService,
    private purposeService: PurposeService,
    private regionalEcosystemStatusService: RegionalEcosystemStatusService,
    private regionalEcosystemService: RegionalEcosystemService,
    private channelDimensionsService: ChannelDimensionsService,
    private logger: Logger,
    private activityService: ActivityService,
    private declarationsService: DeclarationsService,
    private acknowledgementService: AcknowledgementService,
    private reasonService: ReasonService,
    private http: HttpClient
  ) {
    this.formWizardDataModel = sessionService.getSessionData();
  }

  clear() {
    this.formWizardDataModel = new FormWizardDataModel();
    this.sessionService.destroyAndResetSession();
    return this.persistStorage$();
  }

  clearOrReuseLotAfterSubmission(reUseSameLotPlan: boolean) {

    const existingFormSessionData = this.utilService.clone(this.sessionService.getSessionData());
    this.formWizardDataModel = new FormWizardDataModel();
    if (reUseSameLotPlan) {
      this.formWizardDataModel.lotPlan = existingFormSessionData?.lotPlan;
    }
    else {
      this.formWizardDataModel.lotPlan = undefined;
    }
    this.formWizardDataModel.landholderDetails = existingFormSessionData?.landholderDetails;
    this.formWizardDataModel.landholderConfirmation = existingFormSessionData?.landholderConfirmation;
    this.formWizardDataModel.notifierDetails = existingFormSessionData?.notifierDetails;

    return this.persistStorage$();
  }


  /**
   * Returns the completion state for each of the forms in the form wizard steps.
   */
  getWizardDataState(): IWizardDataState {
    const ds: IWizardDataState = {
      isAcknowledgementPopulated: !!this.getAcknowledgementsData(),
      isActivityPopulated: !!this.getActivities(),
      isCategoryPurposePopulated: !!this.getCategories() && this.getCategories().length > 0,
      isCoastalResponsePopulated: !!this.getIsCoastalResponse(),
      isExchangeAreaPopulated: !!this.getExchangeArea(),
      isLandHolderConfirmationPopulated: !!this.getLandholderConfirmationModel(),
      isLandHolderDetailsPopulated: !!this.getLandholderDetailsModel(),
      isNotifierDetailsPopulated: !!this.getNotifierDetailsModel(),
      isLotPlanPopulated: !!this.getLotPlanModel(),
      isNotificationAreaPopulated: !!this.getNotificationArea(),
      isChannelDimensionsPopulated: !!this.getChannelDimensions(),
      isPurposePopulated: !!this.getPurpose(),
      isSelfAuditPopulated: !!this.getSelfAuditEvidence(),
      isRegionalEcosystemPopulated: !!this.getRegionalEcosystems(),
      isRegionalEcosystemStatusPopulated: !!this.getRegionalEcosystemStatuses(),
      isReasonPopulated: !!this.getReason(),
      isDeclarationPopulated: !!this.getDeclarations(),
    };

    return ds;
  }

  getLotPlanModel(): ILotPlanModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.lotPlan);
  }

  /**
   * Updates the lot plan model.
   * @param model the lot plan model we wish to update
   */
  setLotOnPlanModel$(model: ILotPlanModel): Observable<boolean> {
    this.formWizardDataModel.lotPlan = this.utilService.clone(model);

    // clear any subsequent data everytime setLotPlanModel is called - so data is correct
    this.formWizardDataModel.purpose = undefined;
    this.formWizardDataModel.categoryPurposes = undefined;
    this.formWizardDataModel.activities = undefined;
    this.formWizardDataModel.regionalEcosystemSearchResults = undefined;
    this.formWizardDataModel.regionalEcosystemStatus = undefined;
    this.formWizardDataModel.regionalEcosystem = undefined;
    this.formWizardDataModel.isCoastalResponse = false;
    this.formWizardDataModel.notificationArea = undefined;
    this.formWizardDataModel.reasonData = undefined;
    this.formWizardDataModel.channelDimensions = undefined;
    this.formWizardDataModel.exchangeAreaData = undefined;
    this.formWizardDataModel.acknowledgementNotificationData = undefined;
    this.formWizardDataModel.acknowledgementsData = undefined;

    return this.persistStorage$();
  }

  /**
   * Get the landholder confirmation model for landholder-confirmation wizard step
   * @returns the landholder confirmation model
   */
  getLandholderConfirmationModel(): ILandholderConfirmationModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.landholderConfirmation);
  }

  /**
   * Get the landholder details model for landholder-details wizard step
   * @returns the landholder details model
   */
  getLandholderDetailsModel(): ILandholderDetailsModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.landholderDetails);
  }

  /**
   * Updates the landholder details model.
   * @param model the landholder details model we wish to update
   */
  setLandholderDetailsModel$(model: ILandholderDetailsModel): Observable<boolean> {
    this.formWizardDataModel.landholderDetails = this.utilService.clone(model);
    const serverUpdate$ = this.contactService.setLandholderDetails$(model);
    return this.persistStorage$(serverUpdate$);

  }

  /**
  * Get the notifier details model for notifier-details wizard step
  * @returns the notifier details model
  */
  getNotifierDetailsModel(): INotifierDetailsModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.notifierDetails);
  }

  /**
    * Updates the landholder details model.
    * @param model the landholder details model we wish to update
    */
  setNotifierDetailsModel$(model: INotifierDetailsModel): Observable<boolean> {
    this.formWizardDataModel.notifierDetails = this.utilService.clone(model);
    const serverUpdate$ = this.contactService.setNotifierDetails$(model);
    return this.persistStorage$(serverUpdate$);
  }

  /**
   * Updates the landholder confirmation model.
   * @param model the landholder confirmation model we wish to update
   */
  setLandholderConfirmationModel$(model: ILandholderConfirmationModel): Observable<boolean> {
    if (model.areYouTheLandholder === true && this.formWizardDataModel.notifierDetails !== null) {
      this.formWizardDataModel.notifierDetails = undefined;
      model.areYouAuthorised = undefined;
      model.areYouLandsOfficer = undefined;
    }

    this.formWizardDataModel.landholderConfirmation = this.utilService.clone(model);

    const serverUpdate$ = this.contactService.setLandholderConfirmation$(model);
    return this.persistStorage$(serverUpdate$);
  }

  getReason(): IReasonModel[] | undefined {
    return this.utilService.clone(this.formWizardDataModel.reasonData);
  }

  getPurpose(): IPurposeDataModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.purpose);
  }

  setPurpose$(model: IPurposeDataModel): Observable<boolean> {

    // ensure we clear any subsequent data that may have been set - when new and existing data not match
    if (model.purpose && model.purpose.purposeCode != this.formWizardDataModel.purpose?.purpose.purposeCode) {
      this.formWizardDataModel.activities = undefined;
      this.formWizardDataModel.categoryPurposes = undefined;
      this.formWizardDataModel.regionalEcosystemSearchResults = undefined;
      this.formWizardDataModel.regionalEcosystemStatus = undefined;
      this.formWizardDataModel.regionalEcosystem = undefined;
      this.formWizardDataModel.isCoastalResponse = false;
      this.formWizardDataModel.notificationArea = undefined;
      this.formWizardDataModel.reasonData = undefined;
      this.formWizardDataModel.declarationsData = undefined;
      this.formWizardDataModel.channelDimensions = undefined;
      this.formWizardDataModel.exchangeAreaData = undefined;
      this.formWizardDataModel.acknowledgementNotificationData = undefined;
      this.formWizardDataModel.acknowledgementsData = undefined;
    }

    this.formWizardDataModel.purpose = this.utilService.clone(model);
    this.formWizardDataModel.isExchangeAreaRequired = false;

    const sessionId = this.sessionService.getSessionIdOrError();
    const serverUpdate$ = this.purposeService.setPurpose$(model.purpose.purposeCode, sessionId);

    return this.persistStorage$(serverUpdate$);
  }

  setCategories$(model: ICategoryPurposeDataModel[]) {

    // ensure we clear any subsequent data that may have been set - when new and existing data not match
    if (!!this.formWizardDataModel.categoryPurposes && !!model) {
      if (model.length != this.formWizardDataModel.categoryPurposes?.length ||
        !model.every(m => this.formWizardDataModel.categoryPurposes?.some(c => c.categoryPurposeId === m.categoryPurposeId))) {

        this.formWizardDataModel.activities = undefined;
        this.formWizardDataModel.regionalEcosystemSearchResults = undefined;
        this.formWizardDataModel.regionalEcosystemStatus = undefined;
        this.formWizardDataModel.regionalEcosystem = undefined;
        this.formWizardDataModel.isCoastalResponse = false;
        this.formWizardDataModel.notificationArea = undefined;
        this.formWizardDataModel.reasonData = undefined;
        this.formWizardDataModel.channelDimensions = undefined;
        this.formWizardDataModel.declarationsData = undefined;
        this.formWizardDataModel.exchangeAreaData = undefined;
        this.formWizardDataModel.acknowledgementNotificationData = undefined;
        this.formWizardDataModel.acknowledgementsData = undefined;
      }
    }

    this.formWizardDataModel.categoryPurposes = this.utilService.clone(model) ?? [];

    const sessionId = this.sessionService.getSessionIdOrError();
    const serverUpdate$ = this.categoryService.setCategoriesForPurpose$(model, sessionId);
    return this.persistStorage$(serverUpdate$);
  }

  getCategories(): ICategoryPurposeDataModel[] {
    return this.utilService.clone(this.formWizardDataModel.categoryPurposes) ?? [];
  }

  getActivities(): IActivityStorageDataModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.activities);
  }

  setActivities$(model: IActivityStorageDataModel) {
    if (!model.selectedActivities) { return of(false) }

    const sessionId = this.sessionService.getSessionIdOrError();
    const serverUpdate$ = this.activityService.setActivities$(model.selectedActivities, sessionId)
      .pipe(tap(result => {
        // API isExchangeAreaRequired chained Result. this should have been done as as separate service call!
        this.setIsExchangeAreaRequired(result.isExchangeAreaRequired)
      }), map(() => true));

    const existingActivityIds = this.formWizardDataModel.activities != null
      ? this.formWizardDataModel?.activities?.selectedActivities?.map(x =>
        x.activityId)
      : [];

    const newActivityIds = model.selectedActivities.map(x => x.activityId);

    if (existingActivityIds?.length !== newActivityIds.length
      || !existingActivityIds.every(existingActivityId => newActivityIds.some(newActivityId => existingActivityId === newActivityId))) {

      this.formWizardDataModel.channelDimensions = undefined;
      this.formWizardDataModel.regionalEcosystemSearchResults = undefined;
      this.formWizardDataModel.regionalEcosystemStatus = undefined;
      this.formWizardDataModel.regionalEcosystem = undefined;
      this.formWizardDataModel.isCoastalResponse = false;
      this.formWizardDataModel.notificationArea = undefined;
      this.formWizardDataModel.reasonData = undefined;
      this.formWizardDataModel.declarationsData = undefined;
      this.formWizardDataModel.channelDimensions = undefined;
      this.formWizardDataModel.exchangeAreaData = undefined;
      this.formWizardDataModel.acknowledgementNotificationData = undefined;
      this.formWizardDataModel.acknowledgementsData = undefined;

    }

    this.formWizardDataModel.activities = this.utilService.clone(model);
    return this.persistStorage$(serverUpdate$);
  }

  getRegionalEcosystems(): IRegionalEcosystemDataModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.regionalEcosystem);
  }

  getRegionalEcosystemStatuses(): IRegionalEcosystemStatusModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.regionalEcosystemStatus);
  }

  getNotificationArea(): INotificationAreaModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.notificationArea);
  }

  setNotificationArea$(model: INotificationAreaModel): Observable<boolean> {

    this.formWizardDataModel.notificationArea = this.utilService.clone(model);
    const serverUpdate$ = this.notificationAreaService.sessionSetNotificationArea$(model)
      .pipe(
        catchError(err => {
          this.logger.error('setNotificationArea error', err);
          return throwError(err);
        }),
        map((result: ISetAreaResponseModel) => {
          this.formWizardDataModel.isExchangeAreaRequired = result.isExchangeAreaRequired
          return true;
        }
        ));

    return this.persistStorage$(serverUpdate$);
  }

  setRegionalEcosystemStatuses$(model: IRegionalEcosystemStatusModel): Observable<boolean> {
    if (!model.regionalEcosystemStatusIds) { return of(false) }

    const serverUpdate$ = this.regionalEcosystemStatusService.setRegionalEcosystemStatuses$(model).pipe(
      catchError(err => {
        this.logger.error('setRegionalEcosystemStatuses error', err);
        return of(err);
      }),
      map((result: IRegionalEcosystemStatusesResponseModel) => {
        this.formWizardDataModel.isExchangeAreaRequired = result.isExchangeAreaRequired
        return true;
      }));
    this.formWizardDataModel.regionalEcosystemStatus = this.utilService.clone(model);

    return this.persistStorage$(serverUpdate$);
  }

  setRegionalEcosystems$(model: IRegionalEcosystemDataModel): Observable<boolean> {
    if (!model.regionalEcosystems) { return of(false) }

    const serverUpdate$ = this.regionalEcosystemService.setRegionalEcosystems$(model).pipe(
      catchError(err => {
        this.logger.error('setRegionalEcosystem error', err);
        return of(err);
      }),
      map(() => {
        return true;
      }));
    this.formWizardDataModel.regionalEcosystem = this.utilService.clone(model);
    return this.persistStorage$(serverUpdate$);
  }

  setChannelDimensions$(model: IChannelDimensionsDataModel): Observable<boolean> {
    const channelDimensions = {} as IChannelDimensionsModel;
    channelDimensions.width = model.width;
    this.formWizardDataModel.channelDimensions = this.utilService.clone(channelDimensions);

    const serverUpdate$ = this.channelDimensionsService.setChannelDimensions$(model)
      .pipe(
        catchError(err => {
          this.logger.error('setChannelDimensions error', err);
          return throwError(err);
        }),
        map((result: ISetAreaResponseModel) => {
          this.formWizardDataModel.isExchangeAreaRequired = result.isExchangeAreaRequired
          return true;
        }
        ));

    return this.persistStorage$(serverUpdate$);
  }

  getChannelDimensions(): IChannelDimensionsModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.channelDimensions);
  }

  getIsCoastalResponse(): boolean | undefined {
    return this.utilService.clone(this.formWizardDataModel.isCoastalResponse);
  }

  setIsCoastalResponse$(isCoastalResponse: boolean): Observable<boolean> {
    const sessionId = this.sessionService.getSessionIdOrError();
    this.formWizardDataModel.isCoastalResponse = isCoastalResponse;
    const serverUpdate$ = this.notificationAreaService.setIsCoastalResponse$(isCoastalResponse, sessionId);
    return this.persistStorage$(serverUpdate$);
  }

  getIsExchangeAreaRequired(): boolean {
    return this.formWizardDataModel.isExchangeAreaRequired;
  }

  setIsExchangeAreaRequired(isExchangeAreaRequired: boolean) {
    this.formWizardDataModel.isExchangeAreaRequired = isExchangeAreaRequired;
    return this.persistStorage$();
  }

  getIsSelfAuditRequired(): boolean {
    return this.formWizardDataModel.isSelfAuditRequired || false;
  }

  setSelfAuditRequired$(): Observable<boolean> {
    const sessionId = this.sessionService.getSessionIdOrError();

    const url = `${environment.apiUrl}/session/self-audit-required?sessionKey=` + encodeURIComponent(sessionId);
    return this.http.get<boolean>(url)
      .pipe(
        mergeMap((selfAuditRequired) => {
          this.formWizardDataModel.isSelfAuditRequired = selfAuditRequired;
          return this.persistStorage$();
        }));

  }

  setSelfAuditEvidence$(model: ISelfAuditDataModel): Observable<boolean> {
    this.formWizardDataModel.selfAuditData = this.utilService.clone(model);

    const sessionId = this.sessionService.getSessionIdOrError();
    const serverUpdate$ = this.selfAuditService.setSelfAudit$(model, sessionId);

    return this.persistStorage$(serverUpdate$);
  }

  getSelfAuditRequired() {
    return this.formWizardDataModel.isSelfAuditRequired;
  }

  getSelfAuditEvidence(): ISelfAuditDataModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.selfAuditData);
  }

  setExchangeArea$(model: IExchangeAreaDataModel): Observable<boolean> {
    this.formWizardDataModel.exchangeAreaData = this.utilService.clone(model);

    const sessionId = this.sessionService.getSessionIdOrError();
    const serverUpdate$ = this.exchangeAreaService.setSessionExchangeArea$(model, sessionId);

    return this.persistStorage$(serverUpdate$);
  }

  getExchangeArea(): IExchangeAreaDataModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.exchangeAreaData);
  }

  setDeclarationsData$(model: IDeclarationsDataModel): Observable<boolean> {
    this.formWizardDataModel.declarationsData = this.utilService.clone(model);

    const sessionId = this.sessionService.getSessionIdOrError();
    const serverUpdate$ = this.declarationsService.setDeclarations$(model, sessionId);

    return this.persistStorage$(serverUpdate$);
  }

  setFinalizeNotificationReferenceNumber$(notificationReferenceNumber: string): Observable<boolean> {

    this.formWizardDataModel.acknowledgementNotificationData = { notificationReferenceNumber: notificationReferenceNumber } as IAcknowledgementNotificationDataModel;
    return this.persistStorage$();
  }

  getDeclarations(): IDeclarationsDataModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.declarationsData);
  }

  getAcknowledgementNotificationData(): IAcknowledgementNotificationDataModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.acknowledgementNotificationData);
  }

  getAcknowledgementsData(): IAcknowledgementsDataModel | undefined {
    return this.utilService.clone(this.formWizardDataModel.acknowledgementsData)
  }

  setAcknowledgement$(model: IAcknowledgementsDataModel): Observable<boolean> {
    this.formWizardDataModel.acknowledgementsData = this.utilService.clone(model);

    const serverUpdate$ = this.acknowledgementService.setAcknowledgements$(model);
    return this.persistStorage$(serverUpdate$);
  }

  setReason$(model: IReasonModel[]): Observable<boolean> {
    this.formWizardDataModel.reasonData = this.utilService.clone(model);
    const serverUpdate$ = this.reasonService.setReason$(model);
    return this.persistStorage$(serverUpdate$);
  }

  private persistStorage$(serverUpdate$: Observable<boolean> | null = null): Observable<boolean> {
    if (!serverUpdate$) {
      return this.sessionService.setSessionData$(this.formWizardDataModel);
    }

    return combineLatest([
      serverUpdate$,
      this.sessionService.setSessionData$(this.formWizardDataModel)
    ]).pipe(
      map(([serverUpdateResult, localSessionResult]) => {
        return serverUpdateResult && localSessionResult;
      })
    );
  }

  renewSessionWithExistingData$(): Observable<string> {
    const lotOnPlanDetails = this.getLotPlanModel();
    const landholderConfirmation = this.getLandholderConfirmationModel();
    const landholderDetails = this.getLandholderDetailsModel();
    const notifierDetails = this.getNotifierDetailsModel();

    const modelForPost = {
      setLotOnPlanRequestDto: lotOnPlanDetails,
      setLandholderConfirmationRequestDto: landholderConfirmation,
      setLandholderDetailsRequestDto: landholderDetails,
      setNotifierDetailsRequestDto: notifierDetails
    };

    const url = `${environment.apiUrl}/session/start-new-session-with-existing-data`;
    return this.http.post<string>(url, modelForPost);

  }

}
